import type { FailureDiagnostic } from '@chatsdet/types/issues';

type OriginalDiagnosis = {
  /** Row id of the diagnosis the query returned. */
  id: string | null;
  status: string | null;
  diagnostic: FailureDiagnostic | null;
};

/**
 * A repeat failure carries only a thin inherited diagnosis. When the exact
 * original diagnosis (`repeatOf.diagnosticId`) has loaded, show its analysis
 * content instead, keeping the carried `completedAt` and `triage` so the repeat
 * box still renders. Fields describing the other run (timeline, quality
 * warnings, outcome correction, proposal, agent run, …) are deliberately not
 * copied. If the item has since been re-diagnosed (different row id), the
 * carried copy wins.
 */
export function resolveShownDiagnosis(
  carried: FailureDiagnostic | null,
  original: OriginalDiagnosis
): FailureDiagnostic | null {
  const repeatOf = carried?.triage?.repeatOf;
  if (!carried || !repeatOf) return carried;
  if (original.status !== 'completed' || !original.diagnostic) return carried;
  if (original.id !== repeatOf.diagnosticId) return carried;
  const o = original.diagnostic;
  return {
    category: o.category,
    title: o.title,
    failurePoints: o.failurePoints,
    recommendation: o.recommendation,
    smokingGun: o.smokingGun,
    evidence: o.evidence,
    openQuestions: o.openQuestions,
    comparisonWithHistory: o.comparisonWithHistory,
    completedAt: carried.completedAt,
    triage: carried.triage,
  };
}
